/**
 * Uzbek Cyrillic → Latin transliteration for client-side search filtering.
 * Mirror of server/utils/normalize.js (keep in sync).
 */

const CYR_TO_LAT = [
  ['Ш', 'sh'], ['Ч', 'ch'], ['Ё', 'yo'], ['Ю', 'yu'], ['Я', 'ya'],
  ['Ц', 'ts'], ['Щ', 'shch'],
  ['ш', 'sh'], ['ч', 'ch'], ['ё', 'yo'], ['ю', 'yu'], ['я', 'ya'],
  ['ц', 'ts'], ['щ', 'shch'],
  ['А', 'a'], ['Б', 'b'], ['В', 'v'], ['Г', 'g'], ['Д', 'd'],
  ['Е', 'e'], ['Ж', 'j'], ['З', 'z'], ['И', 'i'], ['Й', 'y'],
  ['К', 'k'], ['Л', 'l'], ['М', 'm'], ['Н', 'n'], ['О', 'o'],
  ['П', 'p'], ['Р', 'r'], ['С', 's'], ['Т', 't'], ['У', 'u'],
  ['Ф', 'f'], ['Х', 'x'], ['Э', 'e'],
  ['Ў', 'o'], ['Қ', 'q'], ['Ғ', 'g'], ['Ҳ', 'h'],
  ['Ъ', ''], ['Ь', ''],
  ['а', 'a'], ['б', 'b'], ['в', 'v'], ['г', 'g'], ['д', 'd'],
  ['е', 'e'], ['ж', 'j'], ['з', 'z'], ['и', 'i'], ['й', 'y'],
  ['к', 'k'], ['л', 'l'], ['м', 'm'], ['н', 'n'], ['о', 'o'],
  ['п', 'p'], ['р', 'r'], ['с', 's'], ['т', 't'], ['у', 'u'],
  ['ф', 'f'], ['х', 'x'], ['э', 'e'],
  ['ў', 'o'], ['қ', 'q'], ['ғ', 'g'], ['ҳ', 'h'],
  ['ъ', ''], ['ь', ''],
];

function normalizeBase(text) {
  if (!text) return '';
  let result = text;
  for (const [cyr, lat] of CYR_TO_LAT) {
    result = result.split(cyr).join(lat);
  }
  return result
    .toLowerCase()
    .replace(/[''`]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Normalize text to Latin form.
 */
export default function normalize(text) {
  return normalizeBase(text);
}

/**
 * Extract trigrams from a string (same algorithm as pg_trgm).
 */
function trigrams(str) {
  const padded = `  ${str} `;
  const set = new Set();
  for (let i = 0; i <= padded.length - 3; i++) {
    set.add(padded.slice(i, i + 3));
  }
  return set;
}

/**
 * Calculate trigram similarity between two strings (0..1), matching pg_trgm logic.
 */
function trigramSimilarity(a, b) {
  if (!a || !b) return 0;
  const ta = trigrams(a);
  const tb = trigrams(b);
  let common = 0;
  for (const t of ta) {
    if (tb.has(t)) common++;
  }
  const total = ta.size + tb.size - common;
  return total === 0 ? 0 : common / total;
}

/**
 * Fuzzy match: checks includes first (fast path), then trigram similarity.
 * Mirrors pg_trgm behavior on the client side.
 *
 * Usage: fuzzyIncludes(normalize(dataString), rawQuery)
 */
export function fuzzyIncludes(normalizedHaystack, rawQuery, threshold = 0.3) {
  if (!normalizedHaystack || !rawQuery) return false;
  const q = normalizeBase(String(rawQuery).trim());
  if (q.length < 2) return false;
  // Fast path: exact substring match
  if (normalizedHaystack.includes(q)) return true;
  // Slow path: trigram similarity
  return trigramSimilarity(normalizedHaystack, q) > threshold;
}
